'use strict'

var BemCarouselSlider = require('./BemCarouselSlider')
var BemCarouselTapeSlider = require('./BemCarouselTapeSlider')

var DEFAULTS = {
	// Sliding animation duration (in milliseconds)
	slideDuration: 200,
	autoSlide: true,
	// Waiting time until next auto slide (in milliseconds)
	autoSlideWait: 3000,
	stopAutoSlideOnHover: true,
	// When true 'next' and 'prev' buttons are always visible
	cyclicScrolling: true,
	clickablePrevNext: true,
	// BEM element names
	elements: {
		frame: '__frame',
		tape: '__tape',
		slide: '__slide',
		prev: '__prev',
		next: '__next',
		pill: '__pill'
	},
	// BEM modifiers
	modifiers: {
		current: 'current',
		outgoing: 'outgoing',
		incoming: 'incoming',
		hasNext: 'has-next',
		hasPrev: 'has-prev'
	},
	elementSeparator: '__',
	modifierSeparator: '--',
	// Sliding method (effect). Can be:
	// - null | BemCarouselSlider
	// - 'tape' | BemCarouselTapeSlider
	// - Any class derrived from BemCarouselSlider
	slider: 'tape'
}

/**
 * BEM-aware carousel.
 *
 * Features:
 * - Customizable CSS element naming (with BEM-scheme support)
 * - Pluggable sliding methods (effects)
 *
 * Requirements (ES6 polyfills):
 * - Object
 * - Array
 * - Function
 * - String
 *
 * ```
 * var carousel = new BemCarousel({
 *    el: document.getElementById('carousel')
 *    block: 'product-carousel',
 *    slider: BemCarouselTapeSlider
 * })
 * ```
 *
 * Additional options may be injected by the specified slider.
 */
var BemCarousel = function(config) {
	if (config === undefined)
		throw new Error('Constructor parameters required')

	if (config.el === undefined)
		throw new Error('Constructor requires `el` parameter')
	var el = (config.el instanceof jQuery) ? config.el[0] : config.el;
	this.el = el;
	this.$el = $(el);
	this.$el.data('BemCarousel', this)

	if (config.block === undefined)
		throw new Error('Constructor requires `el` parameter')

	$.extend(true, this, DEFAULTS, config)

	this.$frame = $(this.elementSelector('frame'), el)
	this.$tape = $(this.elementSelector('tape'), el)
	this.$slides = $(this.elementSelector('slide'), el)
	this.$prev = $(this.elementSelector('prev'), el)
	this.$next = $(this.elementSelector('next'), el)
	this.$pills = $(this.elementSelector('pill'), el)

	this.$currentSlide = this.$slides.filter(this.elementSelector('slide', 'current'))
	if (!this.$currentSlide.length) {
		this.$currentSlide = this.$slides.filter(':first')
	}
	this.currentSlideIndex = this.$slides.index(this.$currentSlide)

	this.$currentPill = this.$pills.filter(this.elementSelector('pill', 'current'))
	if (!this.$currentPill.length) {
		this.$currentPill = this.$pills.filter(':first')
	}

	if (typeof this.slider == 'string') {
		switch (this.slider) {
			case 'tape':
				this.slider = BemCarouselTapeSlider
				break
			default:
				throw new Error('Incorrect `slider` option "' + this.slider + '"')
		}
	} else if (!this.slider) {
		this.slider = BemCarouselSlider
	}
	this.slider = new (this.slider)(this)
}

var EVENT_NS = '.bem-carousel'
var EVENT_BEFORE_SLIDE = BemCarousel.EVENT_BEFORE_SLIDE = 'sliding' + EVENT_NS
var EVENT_AFTER_SLIDE = BemCarousel.EVENT_AFTER_SLIDE = 'slide' + EVENT_NS

Object.assign(BemCarousel.prototype, {
	init: function() {
		this.updateTotalWidth()
		this.updateHasPrevNextModifiers()
		this.attachEventHandlers()
		if (this.autoSlide) {
			this.beginAutoSlide()
		}
		this.slider.init()
		return this
	},
	elementSelector: function(element, modifier) {
		return '.' + this.elementClass(element, modifier)
	},
	elementClass: function(element, modifier) {
		var name = this.elements[element]
		if (name.startsWith(this.elementSeparator)) {
			name = this.block + name
		}
		if (modifier !== undefined) {
			name += this.modifierSeparator + this.modifiers[modifier]
		}
		return name
	},
	attachEventHandlers: function() {
		if (this.clickablePrevNext) {
			this.$prev.on('click', this.onPrevBtnClick.bind(this))
			this.$next.on('click', this.onNextBtnClick.bind(this))
		}

		this.$pills.on('click', this.onPillClick.bind(this))

		if (this.autoSlide && this.stopAutoSlideOnHover) {
			this.$el.on('mouseenter', this.onMouseEnter.bind(this))
			this.$el.on('mouseleave', this.onMouseLeave.bind(this))
		}

		$(window).resize(_.throttle(this.onWindowResize.bind(this), 200))
	},
	onNextBtnClick: function(e) {
		e.preventDefault()
		this.slide(1)
	},
	onPrevBtnClick: function(e) {
		e.preventDefault()
		this.slide(-1)
	},
	onPillClick: function(e) {
		e.preventDefault()
		var $pill = $(e.currentTarget)
		var index = this.$pills.index($pill)
		this.slideTo(index)
	},
	/**
	 * Slide right or left by offset.
	 * Negative offsets means sliding left.
	 */
	slide: function(offset) {
		if (offset === undefined) {
			offset = 1
		} else if (!offset) {
			return
		}
		if (offset < -this.$slides.length) {
			offset = -(-offset % this.$slides.length)
		}
		var nextIndex = this.currentSlideIndex + offset
		if (nextIndex < 0) {
			nextIndex = this.$slides.length + nextIndex
		} else if (nextIndex >= this.$slides.length) {
			nextIndex = nextIndex % this.$slides.length
		}

		this.slideTo(nextIndex)
	},
	/**
	 * Slide to specified index.
	 */
	slideTo: function(index) {
		this.slider.slideTo(index)
		this.resetAutoSlideTimer()
		this.updateHasPrevNextModifiers()
	},
	beforeSlide: function(index) {
		this.$el.trigger(EVENT_BEFORE_SLIDE, index)
	},
	afterSlide: function(index) {
		this.$el.trigger(EVENT_AFTER_SLIDE, index)
	},
	hasAutoSlideBegun: function() {
		return this.autoSlideIntervalId !== undefined
	},
	beginAutoSlide: function() {
		if (!this.hasAutoSlideBegun()) {
			var self = this
			this.autoSlideIntervalId = setInterval(function() {
				self.slide(1)
			}, this.autoSlideWait)
		}
	},
	resetAutoSlideTimer: function() {
		if (this.autoSlide) {
			this.stopAutoSlide()
			this.beginAutoSlide()
		}
	},
	stopAutoSlide: function() {
		if (this.hasAutoSlideBegun()) {
			clearInterval(this.autoSlideIntervalId)
			delete this.autoSlideIntervalId
		}
	},
	onMouseEnter: function() {
		if (this.autoSlide && this.stopAutoSlideOnHover) {
			this.stopAutoSlide()
		}
	},
	onMouseLeave: function() {
		if (this.autoSlide && this.stopAutoSlideOnHover) {
			this.beginAutoSlide()
		}
	},
	/**
	 * Updates the `totalWidth` property which is the total
	 * width (including margins) of tape's children.
	 */
	updateTotalWidth: function() {
		var totalWidth = 0
		// Note, tape child may be not only slide (e.g. triads of slides)
		this.$tape.children().each(function(i, el) {
			totalWidth += $(this).outerWidth(true)
		})
		return this.totalWidth = totalWidth
	},
	updateHasPrevNextModifiers: function() {
		var hasPrevClass = this.block + this.modifierSeparator + this.modifiers.hasPrev
		var hasNextClass = this.block + this.modifierSeparator + this.modifiers.hasNext
		this.$el
			.toggleClass(hasPrevClass, this.slider.hasPrev())
			.toggleClass(hasNextClass, this.slider.hasNext())
	},
	onWindowResize: function() {
		this.updateTotalWidth()
		this.updateHasPrevNextModifiers()
	}
})

module.exports = BemCarousel


/*****************
 ** WEBPACK FOOTER
 ** ./components/BemCarousel.js
 ** module id = 163
 ** module chunks = 0
 **/